import {gsap} from "gsap/all";

// The dust the engines blow off the ground as the ship lands. Same idea as
// ThrusterGlow: soft sprites stamped over and over, a few hundred drawImage
// calls a frame instead of a blur.
//
// Two canvases, so the cloud wraps around the legs instead of only sitting
// behind them: one under the ship and one over it. Every puff is given a depth
// when it is born, the ones toward the viewer go on the front canvas and sit a
// little lower on the screen, the rest go behind.
//
// The cloud belongs to the ground, not to the screen: it is centered on the
// shadow the ship casts, which rides up with the surface during the landing,
// and every puff is placed relative to that spot. So the whole cloud travels
// with the ground without either canvas ever being moved.
//
// How much dust is kicked up is strength, 0 to 1, which the landing drives.
// Once it drops to nothing no puff is born, the ones already out drift on and
// settle, and the ticker lets go of itself when the last one is gone.
export class DustCloud {

  constructor($back, $front, $anchor, options = {}) {
    this.$canvases = $back.add($front);
    this.back = this.getContext($back);
    this.front = this.getContext($front);
    this.$anchor = $anchor;

    // Puffs born per second at full strength, and how long each one lives.
    this.emitPerSecond = 90;
    this.lifeMin = 1.6;
    this.lifeMax = 3.2;

    // How wide the strip under the ship they are born along, in screen pixels,
    // about the stance of the legs.
    this.spawnWidth = 700;

    // Nudge of the emitter from the middle of the shadow, in screen pixels.
    this.originOffsetX = -10;
    this.originOffsetY = 40;

    // How fast a puff leaves sideways, in pixels per second, and how quickly
    // it loses that speed, per second. The drag is what makes it roll out and
    // hang instead of flying off the screen.
    this.speedMin = 150;
    this.speedMax = 650;
    this.drag = 0.75;

    // How fast a puff lifts off the ground, in pixels per second.
    this.rise = 0;

    // Puff size in screen pixels, at birth and at death. It grows as it
    // spreads, which is what makes a cloud out of a row of puffs.
    this.sizeStart = 85;
    this.sizeEnd = 295;

    // How strong one puff is at its thickest, before the strength fades it.
    this.alphaMin = 0.15;
    this.alphaMax = 0.31;

    // How far apart the nearest and the farthest puffs sit on the screen, in
    // pixels, which reads as the cloud having depth on the slope.
    this.depthY = 90;

    // What share of the puffs is in front of the ship, and how strong they are
    // compared to the ones behind, so the legs still read through them.
    this.frontShare = 0.4;
    this.frontAlpha = 0.7;

    // Dust of the ground, a puff picks one of these when it is born.
    this.palette = ["#DB7637", "#96502F", "#A73A1A", "#76230F"];

    // The backing store as a share of the canvas box. Dust is soft all over,
    // so drawing it at half the resolution costs nothing to the eye and a
    // quarter of the pixels to the kiosk.
    this.resolution = 0.5;

    Object.assign(this, options);

    this.sprites = this.buildSprites();
    this.particles = [];
    this.strength = 0;
    this.emitCarry = 0;
    this.running = false;
    this.paused = false;

    this.tick = this.tick.bind(this);
    this.sizeCanvases = this.sizeCanvases.bind(this);

    window.addEventListener("resize", this.sizeCanvases);
  }

  getContext($canvas) {
    const canvas = $canvas.get(0);
    return canvas ? {canvas, ctx: canvas.getContext("2d"), top: 0, left: 0} : null;
  }

  // One soft blob per dust color, drawn once and stamped over and over. Wider
  // soft edge than the engines, dust has no core.
  buildSprites() {
    return this.palette.map((color) => {
      const size = 64;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext("2d");
      const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);

      gradient.addColorStop(0, this.withAlpha(color, 1));
      gradient.addColorStop(0.35, this.withAlpha(color, 0.6));
      gradient.addColorStop(1, this.withAlpha(color, 0));

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      return canvas;
    });
  }

  withAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // The backing stores follow the canvas boxes, and where those sit on the
  // screen is kept too, since the puffs are placed in screen pixels.
  sizeCanvases() {
    [this.back, this.front].filter(Boolean).forEach((layer) => {
      const width = layer.canvas.offsetWidth;
      const height = layer.canvas.offsetHeight;
      if (!width || !height) return;

      layer.canvas.width = Math.round(width * this.resolution);
      layer.canvas.height = Math.round(height * this.resolution);
      layer.left = layer.canvas.offsetLeft;
      layer.top = layer.canvas.offsetTop;
    });
  }

  // Where the dust comes from right now, in screen pixels: the middle of the
  // shadow, wherever the ground has carried it, plus the nudge.
  getOrigin() {
    const anchor = this.$anchor.get(0);
    if (!anchor) return {x: 0, y: 0};

    return {
      x: anchor.offsetLeft + anchor.offsetWidth / 2 + gsap.getProperty(anchor, "x") + this.originOffsetX,
      y: anchor.offsetTop + anchor.offsetHeight / 2 + gsap.getProperty(anchor, "y") + this.originOffsetY
    };
  }

  // Takes the strength to a new value over duration seconds, 0 to snap. The
  // ticker starts with the first dust asked for.
  setStrength(strength, duration = 0, ease = "none") {
    gsap.killTweensOf(this, "strength");
    this.startAmbient();

    if (duration <= 0) {
      this.strength = strength;
      return null;
    }

    return gsap.to(this, {
      strength,
      duration,
      ease
    });
  }

  // Frozen with the rest of the run while the "still there?" modal is up.
  setPaused(paused) {
    this.paused = paused;
  }

  startAmbient() {
    if (this.running) return;

    this.running = true;
    this.sizeCanvases();
    gsap.set(this.$canvases, {autoAlpha: 1});
    gsap.ticker.add(this.tick);
  }

  stopAmbient() {
    if (!this.running) return;

    this.running = false;
    gsap.ticker.remove(this.tick);
  }

  spawn() {
    const random = gsap.utils.random;
    const offsetX = random(-0.5, 0.5) * this.spawnWidth;
    const depth = random(-1, 1);

    this.particles.push({
      x: offsetX,
      y: depth * this.depthY * 0.5,
      // Out from the middle, so the two halves of the cloud roll away from
      // the legs on each side.
      vx: Math.sign(offsetX || 1) * random(this.speedMin, this.speedMax),
      vy: -random(0, this.rise),
      front: depth > 1 - this.frontShare * 2,
      age: 0,
      life: random(this.lifeMin, this.lifeMax),
      alpha: random(this.alphaMin, this.alphaMax),
      sprite: this.sprites[Math.floor(Math.random() * this.sprites.length)]
    });
  }

  tick(time, deltaMS) {
    if (!this.running || this.paused) return;

    const dt = Math.min(deltaMS / 1000, 0.05);

    if (this.strength > 0) {
      this.emitCarry += this.emitPerSecond * this.strength * dt;
      while (this.emitCarry >= 1) {
        this.emitCarry -= 1;
        this.spawn();
      }
    }

    const slow = Math.exp(-this.drag * dt);

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;

      if (p.age >= p.life) {
        this.particles.splice(i, 1);
        continue;
      }

      p.vx *= slow;
      p.vy *= slow;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }

    this.draw();

    // Nothing left to blow, nothing on its way up and nothing left in the
    // air: done until asked again. The tween check keeps a strength that is
    // only just setting off from 0 from shutting it down on its first frame.
    if (this.strength <= 0 && !this.particles.length && !gsap.isTweening(this)) this.stopAmbient();
  }

  draw() {
    const origin = this.getOrigin();

    [this.back, this.front].filter(Boolean).forEach((layer) => {
      layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
    });

    this.particles.forEach((p) => {
      const layer = p.front ? this.front : this.back;
      if (!layer) return;

      const t = p.age / p.life;

      // Up fast, down slow, so a puff billows out and takes its time settling.
      const fade = Math.sin(Math.PI * Math.pow(t, 0.4));
      const alpha = fade * p.alpha * (p.front ? this.frontAlpha : 1);
      if (alpha <= 0.002) return;

      const size = (this.sizeStart + (this.sizeEnd - this.sizeStart) * t) * this.resolution;
      const x = (origin.x + p.x - layer.left) * this.resolution;
      const y = (origin.y + p.y - layer.top) * this.resolution;

      layer.ctx.globalAlpha = Math.min(alpha, 1);
      layer.ctx.drawImage(p.sprite, x - size / 2, y - size / 2, size, size);
    });
  }

  // No dust anywhere, ready for the next landing.
  reset() {
    this.stopAmbient();
    gsap.killTweensOf(this);

    this.strength = 0;
    this.emitCarry = 0;
    this.particles = [];
    this.paused = false;

    [this.back, this.front].filter(Boolean).forEach((layer) => {
      layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
    });

    gsap.set(this.$canvases, {autoAlpha: 0});
  }
}
